import React, { useState } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { stripePromise } from '../../../stripe/stripe.js';
import '../../../component-styles/StripePaymentModal.css';
import API_BASE from '../../../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../../../utils/currency.js';

// Staff-side ("designer takes a payment on the client's behalf") Stripe flow
// for proposals. Deliberately proposal-specific and self-contained rather
// than routed through the shared Stripe/StripePaymentModal — that component
// has no fee-preview support, and this endpoint (unlike the public/
// client-portal proposal payment endpoints) is a shared apply-payment route
// that branches on paymentType, so the request body must say `paymentType:
// "stripe"` explicitly or the payment silently records as an unverified
// manual entry instead of going through Stripe verification.
function PaymentForm({ projectId, proposalId, proposalNumber, remainingBalance, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();

  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('idToken');
  const authHeaders = {
    ...(userId ? { userid: userId } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'ach'
  const [achName, setAchName] = useState('');
  const [achEmail, setAchEmail] = useState('');
  const [achStep, setAchStep] = useState('input'); // 'input' | 'mandate'
  const [pendingClientSecret, setPendingClientSecret] = useState('');
  const [pendingFeeCents, setPendingFeeCents] = useState(0);

  const [feePreview, setFeePreview] = useState(null); // { amount, method, feeCents } | null
  const [previewingFee, setPreviewingFee] = useState(false);

  const label = proposalNumber ? `Proposal #${proposalNumber} payment` : 'Proposal payment';

  const validateAmount = (val) => {
    const amt = roundCurrency(val);
    if (!amt || amt < 1) { setError('Amount must be at least $1'); return null; }
    if (amt > remainingBalance) { setError(`Amount cannot exceed $${remainingBalance}`); return null; }
    return amt;
  };

  const createPaymentIntent = async (chargedAmount, pmType = 'card') => {
    const res = await fetch(`${API_BASE}/api/proposals/${projectId}/${proposalId}/create-payment-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ amount: chargedAmount, paymentMethodType: pmType }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.clientSecret) throw new Error(data?.error || 'Missing Stripe client secret');
    return data;
  };

  // Previews the processing-fee surcharge (if the studio has opted in) by
  // creating — and immediately abandoning — a PaymentIntent for the current
  // amount/method, so the staff member sees the fee before charging the
  // client's card. Re-runs whenever the amount is finalized or the method
  // changes.
  const refreshFeePreview = async (pmType = paymentMethod) => {
    const amt = roundCurrency(customAmount);
    if (!amt || amt < 1 || amt > remainingBalance) {
      setFeePreview(null);
      return;
    }
    setPreviewingFee(true);
    try {
      const data = await createPaymentIntent(amt, pmType === 'ach' ? 'us_bank_account' : 'card');
      setFeePreview({ amount: amt, method: pmType, feeCents: Number(data.processingFeeCents || 0) });
    } catch {
      setFeePreview(null);
    } finally {
      setPreviewingFee(false);
    }
  };

  const activeFeePreview =
    feePreview && feePreview.amount === roundCurrency(customAmount) && feePreview.method === paymentMethod
      ? feePreview
      : null;
  const feeDollars = activeFeePreview ? round2ForDisplay(activeFeePreview.feeCents / 100) : 0;
  const totalWithFee = activeFeePreview ? round2ForDisplay(activeFeePreview.amount + feeDollars) : null;

  function round2ForDisplay(v) {
    return Math.round(Number(v || 0) * 100) / 100;
  }

  const recordPayment = async (chargedAmount, intentId) => {
    const res = await fetch(`${API_BASE}/api/proposals/${projectId}/${proposalId}/apply-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        amount: chargedAmount,
        paymentType: 'stripe',
        stripeIntentId: intentId,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Payment was captured, but recording it failed.');
    return data;
  };

  /* ------------------------------------------------------------------ */
  /* CARD FLOW                                                            */
  /* ------------------------------------------------------------------ */
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const amountToCharge = validateAmount(customAmount);
    if (!amountToCharge) return;

    setLoading(true);
    setError('');

    try {
      const { clientSecret } = await createPaymentIntent(amountToCharge, 'card');
      const card = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card } });
      if (result.error) { setError(result.error.message); return; }
      await recordPayment(amountToCharge, result.paymentIntent.id);
      setSuccess(true);
      onSuccess?.(amountToCharge, result.paymentIntent.id);
    } catch (err) {
      setError(err?.message || 'Payment failed. Please try again.');
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------ */
  /* ACH FLOW — STEP 1: COLLECT BANK ACCOUNT                            */
  /* ------------------------------------------------------------------ */
  const handleAchCollect = async (e) => {
    e.preventDefault();
    if (!stripe) return;

    const amountToCharge = validateAmount(customAmount);
    if (!amountToCharge) return;
    if (!achName.trim()) { setError('Account holder name is required'); return; }
    if (!achEmail.trim()) { setError('Email address is required'); return; }

    setLoading(true);
    setError('');

    try {
      const { clientSecret, processingFeeCents } = await createPaymentIntent(amountToCharge, 'us_bank_account');
      setPendingFeeCents(Number(processingFeeCents || 0));

      const { paymentIntent, error: collectError } = await stripe.collectBankAccountForPayment({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: { name: achName.trim(), email: achEmail.trim() },
          },
        },
      });

      if (collectError) { setError(collectError.message); return; }

      if (paymentIntent?.status === 'requires_confirmation') {
        setPendingClientSecret(clientSecret);
        setAchStep('mandate');
      } else {
        setError('Bank account connection was cancelled. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to connect bank account. Please try again.');
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------ */
  /* ACH FLOW — STEP 2: CONFIRM WITH MANDATE ACCEPTANCE                 */
  /* ------------------------------------------------------------------ */
  const handleAchConfirm = async () => {
    if (!stripe || !pendingClientSecret) return;
    setLoading(true);
    setError('');

    try {
      const { paymentIntent, error: confirmError } = await stripe.confirmUsBankAccountPayment(pendingClientSecret);

      if (confirmError) {
        if (confirmError.payment_intent?.status === 'requires_payment_method') {
          setAchStep('input');
          setError('Bank account verification failed. Please try a different account.');
        } else {
          setError(confirmError.message);
        }
        return;
      }

      const amountToCharge = roundCurrency(customAmount);

      // ACH debits confirm to "processing", not "succeeded" — they settle
      // 3-5 business days later, so the backend won't accept a synchronous
      // apply-payment call yet. The payment_intent.succeeded webhook credits
      // the proposal once the debit actually clears.
      if (paymentIntent?.status === 'succeeded') {
        await recordPayment(amountToCharge, paymentIntent.id);
        setSuccess(true);
        onSuccess?.(amountToCharge, paymentIntent.id);
      } else if (paymentIntent?.status === 'processing') {
        setSuccess(true);
        onSuccess?.(amountToCharge, null);
      } else {
        setError('Bank account connection was cancelled. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Payment confirmation failed. Please try again.');
    }

    setLoading(false);
  };

  const switchMethod = (method) => {
    setPaymentMethod(method);
    setError('');
    setAchStep('input');
    setPendingClientSecret('');
    refreshFeePreview(method);
  };

  const feePreviewLine = activeFeePreview && feeDollars > 0 && (
    <p className="spm-fee-preview" style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
      + Processing fee: ${feeDollars.toFixed(2)} — client will be charged ${totalWithFee.toFixed(2)}
    </p>
  );

  /* ------------------------------------------------------------------ */
  /* RENDER                                                               */
  /* ------------------------------------------------------------------ */
  return (
    <div className="spm-overlay payment-popup-overlay">
      <div className="spm-modal payment-popup-panel">
        <h2>{label}</h2>

        {success ? (
          <div className="spm-success">
            <p>
              {paymentMethod === 'ach'
                ? 'ACH payment initiated. Funds typically settle in 3–5 business days.'
                : 'Payment Successful'}
            </p>
            <button className="spm-pay-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            {/* Method Tabs */}
            <div className="spm-method-tabs">
              <button
                type="button"
                className={`spm-method-tab${paymentMethod === 'card' ? ' active' : ''}`}
                onClick={() => switchMethod('card')}
              >
                Card
              </button>
              <button
                type="button"
                className={`spm-method-tab${paymentMethod === 'ach' ? ' active' : ''}`}
                onClick={() => switchMethod('ach')}
              >
                ACH Bank Transfer
              </button>
            </div>

            {/* CARD FORM */}
            {paymentMethod === 'card' && (
              <form onSubmit={handleCardSubmit}>
                <div className="spm-field">
                  <label>Payment Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={remainingBalance}
                    placeholder={`Max: $${remainingBalance}`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(sanitizeCurrencyInput(e.target.value))}
                    onBlur={() => refreshFeePreview('card')}
                    className="spm-amount-input"
                  />
                  {feePreviewLine}
                </div>
                <div className="spm-field">
                  <label>Card Information</label>
                  <CardElement className="spm-card-element" />
                </div>
                {error && <p className="spm-error">{error}</p>}
                <button type="submit" className="spm-pay-btn" disabled={!stripe || loading}>
                  {loading ? 'Processing…' : 'Submit Payment'}
                </button>
                <button type="button" className="spm-cancel" onClick={onClose}>Cancel</button>
              </form>
            )}

            {/* ACH FORM — STEP 1 */}
            {paymentMethod === 'ach' && achStep === 'input' && (
              <form onSubmit={handleAchCollect}>
                <div className="spm-field">
                  <label>Payment Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={remainingBalance}
                    placeholder={`Max: $${remainingBalance}`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(sanitizeCurrencyInput(e.target.value))}
                    onBlur={() => refreshFeePreview('ach')}
                    className="spm-amount-input"
                  />
                  {feePreviewLine}
                </div>
                <div className="spm-field">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={achName}
                    onChange={(e) => setAchName(e.target.value)}
                    className="spm-amount-input"
                    required
                  />
                </div>
                <div className="spm-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    placeholder="client@email.com"
                    value={achEmail}
                    onChange={(e) => setAchEmail(e.target.value)}
                    className="spm-amount-input"
                    required
                  />
                </div>
                <p className="spm-ach-note">
                  The client will be prompted to securely connect their bank account via Stripe.
                </p>
                {error && <p className="spm-error">{error}</p>}
                <button type="submit" className="spm-pay-btn" disabled={!stripe || loading}>
                  {loading ? 'Connecting…' : 'Connect Bank Account'}
                </button>
                <button type="button" className="spm-cancel" onClick={onClose}>Cancel</button>
              </form>
            )}

            {/* ACH FORM — STEP 2: MANDATE */}
            {paymentMethod === 'ach' && achStep === 'mandate' && (
              <div>
                <div className="spm-ach-mandate">
                  <p>
                    By clicking <strong>Confirm Payment</strong>, you authorize the debit of the
                    connected bank account via the ACH network for{' '}
                    <strong>${(roundCurrency(customAmount) + pendingFeeCents / 100).toFixed(2)}</strong>
                    {pendingFeeCents > 0 && ` (including a $${(pendingFeeCents / 100).toFixed(2)} processing fee)`}.
                    This authorization remains in effect until revoked with 30 days' written notice.
                  </p>
                </div>
                {error && <p className="spm-error">{error}</p>}
                <button
                  type="button"
                  className="spm-pay-btn"
                  onClick={handleAchConfirm}
                  disabled={loading}
                >
                  {loading ? 'Confirming…' : 'Confirm Payment'}
                </button>
                <button
                  type="button"
                  className="spm-cancel"
                  onClick={() => { setAchStep('input'); setPendingClientSecret(''); }}
                >
                  Back
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ProposalStripePayment(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
}
